import React, { useState, useEffect, useRef } from 'react';
import {
    Form,
    Button,
    Message,
    Grid,
    Dropdown,
    Checkbox,
    Label,
} from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
    getInvestorProfileByCompanyId,
    createInvestorProfile,
    updateInvestorProfile,
    getInvestorProfileBadges
} from '~/api/company';
import { usePermissions } from '~/requests/permissions/usePermissions';
import CompanyDealExclusionHistory from './CompanyDealExclusionHistory';

import { STATES } from '~/utils';


interface InvestorProfileData {
    id?: string;
    companyId: string;
    maCapacity?: string;
    targetDealSize?: string;
    offTake?: string;
    projectStage?: string;
    states?: string;
    technology?: string;
    badgeIds?: number[];
    isVisible?: boolean;
    fullAccess?: boolean;
}

interface Badge {
    id: number;
    name: string;
    description?: string;
}

interface InvestorProfileProps {
    onProfileSaved?: (profile: InvestorProfileData | null) => void;
}

const InvestorProfile: React.FC<InvestorProfileProps> = ({ onProfileSaved }) => {
    const { id: companyId } = useParams<{ id: string }>();
    const { permissions } = usePermissions();
    const [profile, setProfile] = useState<InvestorProfileData | null>(null);
    const [badges, setBadges] = useState<Badge[]>([]);
    const [loading, setLoading] = useState(true);
    //const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    //const [isEditing, setIsEditing] = useState(false);
    const isInitialLoad = useRef(true);

    // Form state
    const [formData, setFormData] = useState<InvestorProfileData>({
        companyId: companyId || '',
        maCapacity: '',
        targetDealSize: '',
        offTake: '',
        projectStage: '',
        states: '',
        technology: '',
        badgeIds: [],
        isVisible: true,
        fullAccess: false
    });

    const offtakeOptions = [
        { key: 'PPA', text: 'PPA', value: 'PPA' },
        { key: 'COMMUNITY', text: 'Community', value: 'COMMUNITY' },
        { key: 'UTILITY', text: 'Utility', value: 'UTILITY' },
    ];

    const projectStageOptions = [
        { key: 'EARLY_DEV', text: 'Early dev', value: 'EARLY_DEV' },
        { key: 'MID_DEV', text: 'Mid dev', value: 'MID_DEV' },
        { key: 'AT_OR_NEARING_NTP', text: 'At or nearing NTP', value: 'AT_OR_NEARING_NTP' },
        { key: 'MID_CONSTRUCTION', text: 'Mid-Construction', value: 'MID_CONSTRUCTION' },
        { key: 'OPERATING', text: 'Operating', value: 'OPERATING' },
    ];

    const technologyOptions = [
        { key: 'PV', text: 'PV', value: 'PV' },
        { key: 'BESS_PV', text: 'BESS with PV', value: 'BESS_PV' },
        { key: 'BESS_STANDALONE', text: 'BESS standalone', value: 'BESS_STANDALONE' },
        { key: 'MICROGRIDS', text: 'Microgrids', value: 'MICROGRIDS' },
        { key: 'CHP', text: 'CHP', value: 'CHP' },
        { key: 'EV_CHARGING', text: 'EV Charging', value: 'EV_CHARGING' },
        { key: 'OTHER', text: 'Other', value: 'OTHER' },
    ];

    const STATES_WITH_ALL = [{ key: 'ALL', text: 'All States', value: 'ALL' }, ...STATES];

    // Load profile data and badges
    useEffect(() => {
        loadData();
    }, [companyId]);

    useEffect(() => {
        if (!loading && !isInitialLoad.current) {
            const timeoutId = setTimeout(() => {
                handleSubmit();
            }, 500); // Debounce for 500ms
            return () => clearTimeout(timeoutId);
        }

    }, [formData]);

    const loadData = async () => {
        if (!companyId) return;

        try {
            setLoading(true);
            isInitialLoad.current = true;

            // Load badges
            const badgesData = await getInvestorProfileBadges();
            setBadges(badgesData);

            // Try to load existing profile
            try {
                const profileData = await getInvestorProfileByCompanyId(companyId);
                if (profileData) {
                    setProfile(profileData);
                    setFormData({
                        ...profileData,
                        badgeIds: profileData.investorBadges?.map((badge: any) => badge.id) || []
                    });
                }
            } catch (profileError) {
                // Profile doesn't exist yet, that's ok
                //console.log('No existing profile found');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load data');
        } finally {
            setLoading(false);
            setTimeout(() => {
                isInitialLoad.current = false;
            }, 100);
        }
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBadgeToggle = (badgeId: number) => {
        setFormData(prev => ({
            ...prev,
            badgeIds: prev.badgeIds?.includes(badgeId)
                ? prev.badgeIds.filter(id => id !== badgeId)
                : [...(prev.badgeIds || []), badgeId]
        }));
    };

    const handleSubmit = async () => {
        if (!companyId) return;

        //setSaving(true);
        setError(null);

        await toast.promise(
            (async () => {
                if (profile?.id) {
                    // Update existing profile
                    await updateInvestorProfile(profile.id, formData);
                } else {
                    // Create new profile
                    await createInvestorProfile(formData);
                }

                //setIsEditing(false);

                // Reload the profile data
                const updatedProfile = await getInvestorProfileByCompanyId(companyId);
                setProfile(updatedProfile);
                onProfileSaved?.(updatedProfile ?? null);
            })(),
            {
                loading: 'Saving profile...',
                success: 'Investor profile saved successfully!',
                error: (err: any) => err.message || 'Failed to save profile'
            }
        ).catch((err: any) => {
            setError(err.message || 'Failed to save profile');
        }).finally(() => {
            //setSaving(false);
        });
    };

    // Helper function to sort selected values based on their order in the options array
    const sortValuesByOrder = (values: string[], options: Array<{ key: string; value: string }>) => {
        return options
            .filter(option => values.includes(option.value))
            .map(option => option.value);
    };

    // Helper function to handle multi-select changes with sorting
    const handleMultiSelectChange = (field: string, value: any, options: Array<{ key: string; value: string }>) => {
        if (Array.isArray(value)) {
            if (value.includes('ALL')) {
                handleInputChange(field, 'ALL');
            } else {
                // Sort the values based on their order in the options array
                const sortedValues = sortValuesByOrder(value, options);
                handleInputChange(field, sortedValues.join(','));
            }
        } else {
            handleInputChange(field, '');
        }
    };

    const convertStringToArray = (value: string | string[] | undefined): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (value === 'ALL') return ['ALL'];
        return value.split(',').map(v => v.trim()).filter(Boolean);
    };

    return (
        <div className="investor-profile">
            <Grid>
                <Grid.Column width={16}>
                    <h2>Investor Profile</h2>

                    {error && (
                        <Message negative>
                            <Message.Header>Error</Message.Header>
                            <p>{error}</p>
                        </Message>
                    )}

                    {!profile && !loading ? (
                        <Message info>
                            <Message.Header>No Investor Profile</Message.Header>
                            <p>This company doesn&apos;t have an investor profile yet.</p>
                            <Button
                                primary
                                onClick={() => handleSubmit()}
                                icon="plus"
                                content="Create Profile"
                            />
                        </Message>
                    ) : null}

                    {loading ? (
                        <p>Loading...</p>
                    ) : null}

                    {profile && !loading && (
                        <Form>
                            <Grid>

                                <Grid.Column computer={8} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label>Profile visibility</label>
                                        <Checkbox
                                            label={formData.isVisible ? 'Visible' : 'Hidden'}
                                            checked={formData.isVisible || false}
                                            onChange={(e, { checked }) => handleInputChange('isVisible', checked)}
                                            toggle
                                        />
                                    </Form.Field>
                                </Grid.Column>

                                <Grid.Column computer={8} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label>Listings access</label>
                                        <Checkbox
                                            label={formData.fullAccess ? 'Enabled' : 'Disabled'}
                                            checked={formData.fullAccess || false}
                                            onChange={(e, { checked }) => handleInputChange('fullAccess', checked)}
                                            toggle
                                        />
                                    </Form.Field>
                                </Grid.Column>

                                <Grid.Column computer={5} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label>M&A capacity</label>
                                        <Dropdown
                                            selection
                                            options={[
                                                { key: '$500M+', text: '$500M+', value: '$500M+' },
                                                { key: '$200-$500M', text: '$200-$500M', value: '$200-$500M' },
                                                { key: '$100-$200M', text: '$100-$200M', value: '$100-$200M' },
                                                { key: '$50-$100M', text: '$50-$100M', value: '$50-$100M' },
                                                { key: '$20-$50M', text: '$20-$50M', value: '$20-$50M' },
                                                { key: '$10-$20M', text: '$10-$20M', value: '$10-$20M' },
                                                { key: '$5-$10M', text: '$5-$10M', value: '$5-$10M' },
                                                { key: '<$5M', text: '<$5M', value: '<$5M' },
                                            ]}
                                            value={formData.maCapacity || ''}
                                            onChange={(e, { value }) => handleInputChange('maCapacity', value)}
                                            placeholder="Select M&A capacity"
                                            clearable
                                            selectOnBlur={false}
                                        />
                                    </Form.Field>
                                </Grid.Column>

                                <Grid.Column computer={5} tablet={12} mobile={16}>
                                    <Form.Field>
                                        <label>Target deal size</label>
                                        <Dropdown
                                            selection
                                            options={[
                                                { key: '<$1M', text: '<$1M', value: '<$1M' },
                                                { key: '$1M-$5M', text: '$1M-$5M', value: '$1M-$5M' },
                                                { key: '$5M-$10M', text: '$5M-$10M', value: '$5M-$10M' },
                                                { key: '$10M-$25M', text: '$10M-$25M', value: '$10M-$25M' },
                                                { key: '$25M-$50M', text: '$25M-$50M', value: '$25M-$50M' },
                                                { key: '$50M+', text: '$50M+', value: '$50M+' },
                                            ]}
                                            value={formData.targetDealSize || ''}
                                            onChange={(e, { value }) => handleInputChange('targetDealSize', value)}
                                            placeholder="Select target deal size"
                                            clearable
                                            selectOnBlur={false}
                                        />
                                    </Form.Field>
                                </Grid.Column>


                                <Grid.Column computer={5} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label>Offtake</label>
                                        <Dropdown
                                            multiple
                                            selection
                                            options={offtakeOptions}
                                            value={convertStringToArray(formData.offTake)}
                                            onChange={(e, { value }) => handleMultiSelectChange('offTake', value, offtakeOptions)}
                                            placeholder="Select offtake type(s)"
                                            clearable
                                            selectOnBlur={false}
                                        />
                                        {formData.offTake === 'ALL' && (
                                            <Label color="blue" style={{ marginTop: '8px' }}>
                                                All offtake types selected
                                            </Label>
                                        )}
                                    </Form.Field>
                                </Grid.Column>

                                <Grid.Column computer={5} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label>Project stage</label>
                                        <Dropdown
                                            multiple
                                            selection
                                            options={projectStageOptions}
                                            value={convertStringToArray(formData.projectStage)}
                                            onChange={(e, { value }) => handleMultiSelectChange('projectStage', value, projectStageOptions)}
                                            placeholder="Select project stage(s)"
                                            clearable
                                            selectOnBlur={false}
                                        />
                                        {formData.projectStage === 'ALL' && (
                                            <Label color="blue" style={{ marginTop: '8px' }}>
                                                All project stages selected
                                            </Label>
                                        )}
                                    </Form.Field>
                                </Grid.Column>

                                <Grid.Column computer={5} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label>States</label>
                                        <Dropdown
                                            multiple
                                            selection
                                            options={STATES_WITH_ALL}
                                            value={convertStringToArray(formData.states)}
                                            onChange={(e, { value }) => handleMultiSelectChange('states', value, [{ key: 'ALL', text: 'All States', value: 'ALL' }, ...STATES])}
                                            placeholder="Select state(s)"
                                            clearable
                                            selectOnBlur={false}
                                        />

                                        {formData.states === 'ALL' && (
                                            <Label color="blue" style={{ marginTop: '8px' }}>
                                                All states selected
                                            </Label>
                                        )}
                                    </Form.Field>
                                </Grid.Column>

                                <Grid.Column computer={5} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label>Technology</label>
                                        <Dropdown
                                            multiple
                                            selection
                                            options={technologyOptions}
                                            value={convertStringToArray(formData.technology)}
                                            onChange={(e, { value }) => handleMultiSelectChange('technology', value, technologyOptions)}
                                            placeholder="Select technology type(s)"
                                            clearable
                                            selectOnBlur={false}
                                        />
                                        {formData.technology === 'ALL' && (
                                            <Label color="blue" style={{ marginTop: '8px' }}>
                                                All technologies selected
                                            </Label>
                                        )}
                                    </Form.Field>
                                </Grid.Column>




                                <Grid.Column computer={10} tablet={12} mobile={12}>
                                    <Form.Field>
                                        <label style={{ marginBottom: '12px' }}>Investor badges</label>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                gap: '12px 24px',
                                            }}
                                        >
                                            {badges?.map(badge => (
                                                <Checkbox
                                                    key={badge.id}
                                                    label={
                                                        <label>
                                                            <div style={{ fontWeight: 600, fontSize: '14px' }}>{badge.name}</div>
                                                            <div style={{ fontSize: '12px', color: '#888' }}>{badge.description}</div>
                                                        </label>
                                                    }
                                                    checked={formData.badgeIds?.includes(badge.id)}
                                                    onChange={(e, { checked }) => handleBadgeToggle(badge.id)}
                                                    style={{ display: 'block', marginBottom: '8px' }}
                                                />
                                            ))}
                                        </div>
                                        {badges.length === 0 && (
                                            <p style={{ fontStyle: 'italic', color: '#999' }}>
                                                No badges available
                                            </p>
                                        )}
                                    </Form.Field>
                                </Grid.Column>



                            </Grid>

                        </Form>
                    )}

                    {permissions?.isAdmin && companyId ? (
                        <CompanyDealExclusionHistory companyId={companyId} />
                    ) : null}
                </Grid.Column>
            </Grid>
        </div>
    );
};

export default InvestorProfile;